// ============================================================================
// Sub Replacement — COO approval queue.
//
// Visible only to has_full_am_authority roles (superuser/executive/national_am).
// The prompt's "coo/cfo/national_am/superuser" set maps onto this helper —
// the DB enforces it via the BEFORE UPDATE trigger applied in migration
// sub_replacement_enforce_approval_scope_2026_05_12.
//
// Layout: sortable list of Pending requests (urgency tier first — Emergency
// outranks Soon outranks Planned — then date_requested asc so the oldest
// pending request comes to the top within a tier). Each row expands to show
// the full reason + notes + outgoing assignment context, with inline
// Approve/Reject actions.
// ============================================================================

import { canApproveSubReplacement } from '../../shared/role-gates';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, ChevronDown, ChevronRight, RefreshCw, X } from 'lucide-react';
import { useAuth } from '../../shared/auth';
import {
  Button,
  EmptyState,
  ErrorState,
  PageHeader,
  Skeleton,
  UrgencyBadge,
  useToast,
} from '../../shared/components';
import type { SubReplacementRequestRow } from '../../shared/types';
import { SUB_REPLACEMENT_URGENCY_RANK } from '../../shared/types';
import { rpcErrorMessage } from '../../shared/rpc-error';
import { useSubReplacementRequests } from './hooks/use-sub-replacement-data';
import { routeRecord } from '../routing/hooks/use-routing-data';
import { supabase } from '../../shared/supabase';
import { SubChangeReasonTag, useSubChangeReasons } from './sub-change-reasons';
import { incomingPayText } from './pay-unit';

// Finance approves alongside has_full_am_authority — sub replacements carry
// financial implications (hold-pay, incoming sub rate). RLS: srr_update_finance.
function isApprover(role: string | null | undefined): boolean {
  return canApproveSubReplacement(role);
}

// The desk that picks up approved sub replacements for accounts-payable setup is
// HR. ⛔ Resolved from the POSITION at call time, never hard-coded to a person
// (TCSD-0074 / TCSD-0158). A hard-coded EID sat here and survived the first sweep
// because the constant was upper-case and the sweep's grep only looked for the
// name as written. ⛔ TCSD-0160: if nobody holds HR that is a fault to report, not
// a person to guess — the approval still stands and the toast says nobody was told.
async function hrSeatEid(): Promise<string | null> {
  const { data, error } = await supabase.rpc('hr_position_holders', { p_position_key: 'hr' });
  if (error || !data || data.length === 0) return null;
  return (data as { employee_id: string }[])[0]?.employee_id ?? null;
}

export default function SubReplacementApprovalQueue() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const canApprove = isApprover(profile?.role);

  const { data, isLoading, error, refetch, lastUpdated, updateStatus } =
    useSubReplacementRequests({ statuses: ['Pending'] });

  const sorted = useMemo(() => {
    const rows = [...data];
    rows.sort((a, b) => {
      const rankA = SUB_REPLACEMENT_URGENCY_RANK[a.urgency] ?? 99;
      const rankB = SUB_REPLACEMENT_URGENCY_RANK[b.urgency] ?? 99;
      if (rankA !== rankB) return rankA - rankB;
      // Older first (FIFO within tier).
      return (a.created_at ?? '').localeCompare(b.created_at ?? '');
    });
    return rows;
  }, [data]);

  if (!canApprove) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <ErrorState
          title="Not authorized"
          body="The approval queue is restricted to approver roles (superuser / executive / national_am / finance)."
          actions={
            <Button
              variant="secondary"
              onClick={() => navigate('/sub-replacement')}
            >
              Back to Sub Replacement
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl p-4 pb-28 sm:p-6">
      <PageHeader
        title="Sub Replacement — Approval queue"
        meta={
          isLoading
            ? 'Loading…'
            : `${sorted.length} pending${
                lastUpdated
                  ? ` · updated ${lastUpdated.toLocaleTimeString()}`
                  : ''
              }`
        }
        actions={
          <>
            <Button
              variant="ghost"
              size="sm"
              icon={<RefreshCw className="h-4 w-4" />}
              onClick={refetch}
              aria-label="Refresh"
            />
            <Button
              variant="secondary"
              size="sm"
              icon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => navigate('/sub-replacement')}
            >
              Back
            </Button>
          </>
        }
      />

      {error && (
        <div className="mt-4">
          <ErrorState title="Failed to load" body={error} onRetry={refetch} />
        </div>
      )}

      {!error && isLoading && (
        <div className="mt-6 space-y-3">
          <Skeleton className="h-16 rounded-md" />
          <Skeleton className="h-16 rounded-md" />
          <Skeleton className="h-16 rounded-md" />
        </div>
      )}

      {!error && !isLoading && sorted.length === 0 && (
        <div className="mt-10">
          <EmptyState
            title="Nothing pending"
            body="The Sub Replacement queue is empty — no requests are waiting on a decision."
          />
        </div>
      )}

      {!error && !isLoading && sorted.length > 0 && (
        <ul className="mt-6 space-y-2">
          {sorted.map((row) => (
            <QueueRow
              key={row.request_id}
              row={row}
              onDecided={refetch}
              updateStatus={updateStatus}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

interface QueueRowProps {
  row: SubReplacementRequestRow;
  onDecided: () => void;
  updateStatus: (
    requestId: string,
    next: 'Approved' | 'Rejected',
    extra?: { rejection_reason?: string | null },
  ) => Promise<void>;
}

function QueueRow({ row, onDecided, updateStatus }: QueueRowProps) {
  const { reasons } = useSubChangeReasons();
  const [open, setOpen] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toast = useToast();
  const facilityLabel = row.facilities?.facility_name ?? row.facility_id;
  const accountLabel = row.accounts?.account_name ?? row.account_id;
  const requesterLabel = formatEid(row.requester, row.requested_by_eid);
  const daysSince = daysAgo(row.created_at);

  async function onApprove() {
    setSubmitting(true);
    setError(null);
    try {
      await updateStatus(row.request_id, 'Approved');
      // Task 4 — hand the approved request to HR via the routing primitive so it
      // lands in that desk's Inbox. Non-fatal: the approval stands even if the
      // notification fails.
      try {
        const hrEid = await hrSeatEid();
        if (!hrEid) {
          toast.info('Approved', {
            description: 'Nobody holds HR right now, so nobody was notified — tell HR directly.',
          });
        } else {
          await routeRecord({
            entityType: 'sub_replacement_request',
            entityId: row.request_id,
            routedToEid: hrEid,
            reason: `Sub replacement ${row.request_id} approved (${facilityLabel}). Please set up AP for the incoming sub.`,
          });
          toast.success('Approved', { description: 'Routed to HR for AP.' });
        }
      } catch (notifyErr) {
        console.warn('[sub-replacement] AP notify failed', notifyErr);
        toast.info('Approved', {
          description: 'Couldn’t auto-notify HR — tell them directly.',
        });
      }
      onDecided();
    } catch (err) {
      setError(rpcErrorMessage(err, 'Approve failed.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function onReject() {
    if (!rejectionReason.trim()) {
      setError('A rejection reason is required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await updateStatus(row.request_id, 'Rejected', {
        rejection_reason: rejectionReason.trim(),
      });
      setRejecting(false);
      setRejectionReason('');
      onDecided();
    } catch (err) {
      setError(rpcErrorMessage(err, 'Reject failed.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <li className="rounded-md border border-tcs-border bg-tcs-bg">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left"
        aria-expanded={open}
      >
        <div className="flex min-w-0 items-center gap-2">
          {open ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-tcs-text-muted" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-tcs-text-muted" />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-medium text-tcs-text">
              <span className="font-mono text-xs text-tcs-text-muted">
                {row.request_id}
              </span>
              <span className="truncate">{facilityLabel}</span>
            </div>
            <div className="mt-0.5 truncate text-xs text-tcs-text-secondary">
              {accountLabel} · {requesterLabel} · {daysSince}
            </div>
            {row.reason_code && (
              <div className="mt-1">
                <SubChangeReasonTag code={row.reason_code} reasons={reasons} />
              </div>
            )}
            {row.reason && (
              <div className="mt-1 line-clamp-1 text-xs text-tcs-text-muted">
                {row.reason}
              </div>
            )}
          </div>
        </div>
        <div className="shrink-0">
          <UrgencyBadge tier={row.urgency} />
        </div>
      </button>

      {open && (
        <div className="border-t border-tcs-border px-3 py-3">
          <div className="grid gap-3 text-sm sm:grid-cols-2">
            {/* AFB-0051 — surface the incoming (new) sub by name so approvers
                can see who they're approving. Resolved via partner_name. */}
            <Field label="New sub (incoming)">
              {row.incoming_provider?.partner_name
                ?? row.incoming_provider_id
                ?? 'Not yet identified'}
            </Field>
            <Field label="Previous sub">
              {row.outgoing_assignment_id == null
                ? 'No previous sub (first assignment)'
                : row.outgoing_assignment
                  ? `${row.outgoing_assignment.role_title ?? '—'} · ${
                      row.outgoing_assignment.provider_name
                      ?? row.outgoing_assignment.provider_id
                      ?? 'unassigned'
                    } · ${row.outgoing_assignment.assignment_id}`
                  : '—'}
            </Field>
            <Field label="Final payment per agreement (outgoing)">{row.hold_pay ? 'On' : 'Off'}</Field>
            {/* AFB-0027 — pay/incentives/date visible at approval time. */}
            <Field label="Incoming contract rate">
              {incomingPayText(row.incoming_pay_rate, row.outgoing_assignment)}
            </Field>
            <Field label="Performance payments">
              {[
                row.nmi_enabled
                  ? `Scope Performance${row.nmi_amount != null ? ` $${row.nmi_amount}/mo` : ' (amt TBD)'}`
                  : null,
                row.compliment_enabled
                  ? `Compliment${row.compliment_amount != null ? ` $${row.compliment_amount}` : ' (amt TBD)'}`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ') || 'None'}
            </Field>
            <Field label="Date of change">
              {row.planned_change_date
                ? `${row.planned_change_date}${row.change_date_type === 'historical' ? ' (historical)' : ''}`
                : 'Not set'}
            </Field>
            <Field label="Market">
              {row.facilities?.market_region ?? '—'}
            </Field>
            <Field label="Submitted">
              {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
            </Field>
            {(row.reason || row.reason_code) && (
              <Field label="Why" wide>
                {row.reason_code && (
                  <span className="mb-1 block">
                    <SubChangeReasonTag code={row.reason_code} reasons={reasons} />
                  </span>
                )}
                {row.reason && <span className="whitespace-pre-wrap">{row.reason}</span>}
              </Field>
            )}
            {row.notes && (
              <Field label="Notes" wide>
                <span className="whitespace-pre-wrap">{row.notes}</span>
              </Field>
            )}
          </div>

          {error && (
            <div className="mt-3 rounded-md border border-rag-red/30 bg-rag-red/5 px-3 py-2 text-sm text-rag-red">
              {error}
            </div>
          )}

          {!rejecting ? (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button
                icon={<Check className="h-4 w-4" />}
                onClick={onApprove}
                loading={submitting}
              >
                Approve
              </Button>
              <Button
                variant="destructive"
                icon={<X className="h-4 w-4" />}
                onClick={() => setRejecting(true)}
                disabled={submitting}
              >
                Reject
              </Button>
            </div>
          ) : (
            <div className="mt-4 space-y-2">
              <label className="block text-sm font-medium text-tcs-text">
                Rejection reason (required)
              </label>
              <textarea
                className="min-h-[60px] w-full rounded-md border border-tcs-border bg-tcs-bg px-3 py-2 text-base text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setRejecting(false);
                    setError(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={onReject}
                  loading={submitting}
                >
                  Submit rejection
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function Field({
  label,
  wide,
  children,
}: {
  label: string;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={wide ? 'sm:col-span-2' : undefined}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
        {label}
      </div>
      <div className="mt-0.5 text-sm text-tcs-text">{children}</div>
    </div>
  );
}

function formatEid(
  person: {
    employee_name_first: string | null;
    employee_name_last: string | null;
  } | null,
  eid: string | null,
): string {
  if (!person && !eid) return '—';
  if (!person) return eid ?? '—';
  const first = person.employee_name_first ?? '';
  const last = person.employee_name_last ?? '';
  const name = `${first} ${last}`.trim();
  return name || (eid ?? '—');
}

function daysAgo(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '—';
  const diff = Date.now() - ts;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days <= 0) return 'today';
  if (days === 1) return '1 day';
  return `${days} days`;
}
